import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

export interface AddCompanyDialogData {
  companyName: string;
}

export interface AddCompanyDialogResult {
  companyName: string;
  companyType: string;
}

// Custom validator for alphabetic characters only
function alphabeticValidator(control: AbstractControl): ValidationErrors | null {
  const value = control.value;
  if (!value) return null;
  
  const alphabeticRegex = /^[a-zA-Z\s]+$/;
  return alphabeticRegex.test(value) ? null : { alphabetic: true };
}

@Component({
  selector: 'app-add-company-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatIconModule
  ],
  template: `
    <h2 mat-dialog-title class="perse-dialog-title">
      <mat-icon color="primary">add_business</mat-icon>
      Tell us about your company
    </h2>
    
    <mat-dialog-content>
      <form [formGroup]="companyForm" class="company-form">
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Company Name</mat-label>
          <input matInput formControlName="companyName" placeholder="Enter your company name">
          @if (companyForm.get('companyName')?.errors?.['required']) {
            <mat-error>Company name is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Company Type</mat-label>
          <mat-select formControlName="companyType">
            <mat-option value="SOLE_TRADER">Sole Trader</mat-option>
            <mat-option value="PARTNERSHIP_LLP">Partnership/LLP</mat-option>
            <mat-option value="LTD">LTD</mat-option>
            <mat-option value="CHARITY">Charity/Not for Profit</mat-option>
            <mat-option value="PLC">PLC</mat-option>
            <mat-option value="COMMUNITY_CHURCH">Community/Church</mat-option>
            <mat-option value="OTHER">Other</mat-option>
          </mat-select>
          @if (companyForm.get('companyType')?.errors?.['required']) {
            <mat-error>Company type is required</mat-error>
          }
        </mat-form-field>

        @if (isOtherSelected()) {
          <mat-form-field appearance="outline" class="full-width">
            <mat-label>Specify Company Type</mat-label>
            <input matInput 
                   formControlName="otherCompanyType" 
                   placeholder="Enter company type">
            @if (companyForm.get('otherCompanyType')?.errors?.['required']) {
              <mat-error>Please specify the company type</mat-error>
            }
            @if (companyForm.get('otherCompanyType')?.errors?.['alphabetic']) {
              <mat-error>Only letters and spaces are allowed</mat-error>
            }
          </mat-form-field>
        }
      </form>
    </mat-dialog-content>

    <mat-dialog-actions align="end">
      <button mat-button (click)="onCancel()">Cancel</button>
      <button mat-raised-button 
              color="primary" 
              [disabled]="companyForm.invalid"
              (click)="onSave()">
        Save
      </button>
    </mat-dialog-actions>
  `,
  styles: [`
    .company-form {
      display: flex;
      flex-direction: column;
      gap: 16px;
      min-width: 300px;
      padding: 8px 0;
    }

    .full-width {
      width: 100%;
    }

    h2 {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 0 0 16px 0;
      
      mat-icon {
        font-size: 24px;
        width: 24px;
        height: 24px;
      }
    }

    mat-dialog-actions {
      gap: 8px;
    }
  `]
})
export class AddCompanyDialogComponent {
  companyForm: FormGroup;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<AddCompanyDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddCompanyDialogData
  ) {
    this.companyForm = this.fb.group({
      companyName: [data.companyName || '', Validators.required],
      companyType: ['LTD', Validators.required],
      otherCompanyType: ['']
    });

    // Add conditional validators for otherCompanyType
    this.companyForm.get('companyType')?.valueChanges.subscribe(value => {
      const otherCompanyTypeControl = this.companyForm.get('otherCompanyType');
      if (value === 'OTHER') {
        otherCompanyTypeControl?.setValidators([Validators.required, alphabeticValidator]);
      } else {
        otherCompanyTypeControl?.clearValidators();
        otherCompanyTypeControl?.setValue('');
      }
      otherCompanyTypeControl?.updateValueAndValidity();
    });
  }

  isOtherSelected(): boolean {
    return this.companyForm.get('companyType')?.value === 'OTHER';
  }

  onSave(): void {
    if (this.companyForm.valid) {
      const companyTypeValue = this.companyForm.get('companyType')?.value;
      const finalCompanyType = companyTypeValue === 'OTHER' 
        ? this.companyForm.get('otherCompanyType')?.value 
        : companyTypeValue;

      const result: AddCompanyDialogResult = {
        companyName: this.companyForm.get('companyName')?.value,
        companyType: finalCompanyType
      };
      this.dialogRef.close(result);
    }
  }

  onCancel(): void {
    this.dialogRef.close();
  }
}
